/**
 * RequestDetails — Activity and notes methods.
 * Mixed into RequestDetails.prototype.
 */
import { apiClient } from '../../utils/ApiClient.js';

export const ActivityMixin = {

    async handleNoteSubmit(e) {
        const form = e.target;
        if (!this.activeRequestId) return;

        // Guards against a double-click (or double-fire submit event) sending
        // the same note twice, which duplicates the activity log entry and
        // fans out a duplicate notification to every recipient (admins/ROI/CM).
        if (form.dataset.submitting === '1') return;

        const noteField  = form.querySelector('textarea[name="note"]');
        const nonceField = form.querySelector('input[name="request_activity_nonce"]');
        if (!noteField || !nonceField) return;

        const note = noteField.value.trim();
        if (!note) return;

        const formData = new URLSearchParams();
        formData.append('action', 'add_request_activity_note');
        formData.append('request_id', this.activeRequestId);
        formData.append('note', note);
        formData.append('request_activity_nonce', nonceField.value);

        const internalCheckbox = form.querySelector('input[name="is_internal"]');
        if (internalCheckbox && internalCheckbox.checked) {
            formData.append('is_internal', '1');
        }

        form.dataset.submitting = '1';
        const submitBtn = form.querySelector('button[type="submit"]');
        if (submitBtn) submitBtn.disabled = true;

        try {
            const response = await fetch(this.ajaxUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: formData.toString(),
            });
            const result = await response.json();
            if (result.success) {
                this.refreshActivityLists(result.data || {});
                document.querySelectorAll(`${this.noteFormSelector} textarea[name="note"]`).forEach((el) => {
                    el.value = '';
                });
                window.DocuswiftPortal?.components?.notification?.refreshCounts();
            } else {
                console.error(result.data?.message || 'Failed to save note');
            }
        } catch (error) {
            console.error('Note submit error:', error);
        } finally {
            delete form.dataset.submitting;
            if (submitBtn) submitBtn.disabled = false;
        }
    },

    handleActivityDelete(btn) {
        const activityId = btn.dataset.activityId;
        if (!activityId || !this.activeRequestId) return;

        const modal = document.getElementById('activity_delete_modal');
        if (!modal) return;

        // Open modal
        modal.style.display = 'block';
        requestAnimationFrame(() => { modal.style.opacity = '1'; });

        const closeModal = () => {
            modal.style.opacity = '0';
            setTimeout(() => { modal.style.display = 'none'; }, 200);
            confirmBtn.removeEventListener('click', onConfirm);
            cancelBtns.forEach(el => el.removeEventListener('click', onCancel));
            modal.querySelector('.overlay')?.removeEventListener('click', onCancel);
        };

        const onCancel = (e) => { e.preventDefault(); closeModal(); };

        const onConfirm = async () => {
            closeModal();
            btn.disabled = true;

            try {
                const formData = new URLSearchParams();
                formData.append('action', 'docuswift_delete_activity_item');
                formData.append('activity_id', activityId);
                formData.append('nonce', window.docuswiftPortal?.nonce || '');

                const response = await fetch(this.ajaxUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    body: formData.toString(),
                });
                const result = await response.json();
                if (result.success) {
                    const item = btn.closest('.note_item');
                    if (item) {
                        item.style.transition = 'opacity 0.2s';
                        item.style.opacity = '0';
                        setTimeout(() => item.remove(), 200);
                    }
                } else {
                    console.error('Delete failed:', result.data?.message);
                    btn.disabled = false;
                }
            } catch (error) {
                console.error('Delete activity error:', error);
                btn.disabled = false;
            }
        };

        const confirmBtn = modal.querySelector('#activity_delete_confirm');
        const cancelBtns = modal.querySelectorAll('[data-activity-action="cancel"]');

        confirmBtn.addEventListener('click', onConfirm);
        cancelBtns.forEach(el => el.addEventListener('click', onCancel));
        modal.querySelector('.overlay')?.addEventListener('click', onCancel);
    },

    handleActivityEdit(btn) {
        const activityId = btn.dataset.activityId;
        const currentMessage = btn.dataset.activityMessage || '';
        const isInternal = btn.dataset.activityInternal === '1';
        if (!activityId || !this.activeRequestId) return;

        const modal = document.getElementById('activity_edit_modal');
        if (!modal) return;

        const textarea = modal.querySelector('#activity_edit_message');
        const internalCheckbox = modal.querySelector('#activity_edit_internal');

        if (textarea) textarea.value = currentMessage;
        if (internalCheckbox) internalCheckbox.checked = isInternal;

        modal.style.display = 'block';
        requestAnimationFrame(() => { modal.style.opacity = '1'; });
        if (textarea) textarea.focus();

        const confirmBtn = modal.querySelector('#activity_edit_confirm');
        const cancelBtns = modal.querySelectorAll('[data-activity-action="cancel"]');

        const closeModal = () => {
            modal.style.opacity = '0';
            setTimeout(() => { modal.style.display = 'none'; }, 200);
            confirmBtn.removeEventListener('click', onConfirm);
            cancelBtns.forEach(el => el.removeEventListener('click', onCancel));
            modal.querySelector('.overlay')?.removeEventListener('click', onCancel);
        };

        const setLoading = (loading) => {
            confirmBtn.disabled = loading;
            cancelBtns.forEach(el => { el.disabled = loading; });
            modal.querySelectorAll('[data-activity-action="cancel"]').forEach(el => {
                el.style.pointerEvents = loading ? 'none' : '';
                el.style.opacity = loading ? '0.5' : '';
            });
            if (loading) {
                confirmBtn.dataset.originalText = confirmBtn.textContent;
                confirmBtn.innerHTML = '<span class="ds-btn-spinner"></span>';
            } else {
                confirmBtn.textContent = confirmBtn.dataset.originalText || 'Save Changes';
            }
        };

        const onCancel = (e) => { e.preventDefault(); closeModal(); };

        const onConfirm = async () => {
            const message = textarea ? textarea.value.trim() : '';
            if (!message) return;

            setLoading(true);

            try {
                const formData = new URLSearchParams();
                formData.append('action', 'docuswift_update_activity_item');
                formData.append('activity_id', activityId);
                formData.append('message', message);
                formData.append('is_internal', internalCheckbox && internalCheckbox.checked ? '1' : '0');
                formData.append('nonce', window.docuswiftPortal?.nonce || '');

                const response = await fetch(this.ajaxUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    body: formData.toString(),
                });
                const result = await response.json();
                if (result.success) {
                    closeModal();
                    this.fetchDetails(String(this.activeRequestId));
                } else {
                    console.error('Edit failed:', result.data?.message);
                    setLoading(false);
                }
            } catch (error) {
                console.error('Edit activity error:', error);
                setLoading(false);
            }
        };

        confirmBtn.addEventListener('click', onConfirm);
        cancelBtns.forEach(el => el.addEventListener('click', onCancel));
        modal.querySelector('.overlay')?.addEventListener('click', onCancel);
    },

    refreshActivityLists(data) {
        if (!this.details) return;
        const notesEl    = this.details.querySelector('.request-activity-list--notes');
        const activityEl = this.details.querySelector('.request-activity-list--activity');
        if (data.notes_html    && notesEl)    notesEl.innerHTML    = data.notes_html;
        if (data.activity_html && activityEl) activityEl.innerHTML = data.activity_html;
    },

    async loadMoreActivity(button) {
        if (!this.activeRequestId || !button) return;

        const listEl = button.closest('.request-activity-list');
        if (!listEl) return;

        const requestId  = button.dataset.requestId || String(this.activeRequestId);
        const offset     = button.dataset.offset    || '0';
        const limit      = button.dataset.limit     || '5';
        const categories = button.dataset.categories || '';
        const listKey    = button.dataset.listKey   || 'all';

        this.setBtnLoading(button, true);

        try {
            const result = await apiClient.post('load_request_activity', {
                request_id: requestId,
                offset,
                limit,
                categories,
                list_key: listKey,
            });

            if (!result.success) {
                console.error(result.data?.message || 'Failed to load activity');
                this.setBtnLoading(button, false);
                return;
            }

            const { items_html = '', next_button_html = '' } = result.data || {};
            button.remove();
            if (items_html)       listEl.insertAdjacentHTML('beforeend', items_html);
            if (next_button_html) listEl.insertAdjacentHTML('beforeend', next_button_html);
        } catch (error) {
            console.error('Load activity error:', error);
            this.setBtnLoading(button, false);
        }
    },
};
